import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, FileSpreadsheet, FileText, Loader2, Table2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { OMS_COLORS } from '../../dashboard/omsDashTheme';
import { windowLabel } from '../../dashboard/utils/scopeWindow';
import {
  exportAdminReport,
  type AdminExportFormat,
  type AdminReportPayload,
} from '../utils/adminExport';

const FORMATS: Array<{ key: AdminExportFormat; label: string; icon: typeof FileText }> = [
  { key: 'pdf', label: 'PDF report', icon: FileText },
  { key: 'xlsx', label: 'Excel (XLSX)', icon: FileSpreadsheet },
  { key: 'csv', label: 'CSV', icon: Table2 },
];

/** Download menu — WYSIWYG against the current scope + granularity. */
export default function AdminExportMenu({ getPayload }: { getPayload: () => AdminReportPayload | null }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<AdminExportFormat | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = (format: AdminExportFormat) => {
    const payload = getPayload();
    if (!payload || payload.breakdown.length === 0) {
      toast.error('Nothing to export in the current view.');
      setOpen(false);
      return;
    }
    setBusy(format);
    window.setTimeout(() => {
      try {
        exportAdminReport(payload, format);
        toast.success(
          `Report downloaded — ${payload.scopePath[payload.scopePath.length - 1]} · ${windowLabel(payload.window)} · ${payload.granularity} granularity`
        );
      } catch {
        toast.error('Export failed — please try again.');
      } finally {
        setBusy(null);
        setOpen(false);
      }
    }, 60);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[11px] font-extrabold uppercase tracking-[0.08em] text-white transition hover:bg-[#0E4A52]"
        style={{ backgroundColor: '#14606B', border: '1px solid rgba(14,74,82,0.6)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)' }}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        {busy ? 'Preparing…' : 'Download report'}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-11 z-40 w-52 overflow-hidden rounded-xl border border-[rgba(11,59,46,0.28)] p-1 shadow-[0_18px_44px_-20px_rgba(42,31,23,0.40)]"
            style={{ backgroundColor: 'rgba(250,247,238,0.98)' }}
          >
            <p className="px-2.5 pb-1 pt-1.5 text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: OMS_COLORS.caption }}>
              Exports exactly what you see
            </p>
            {FORMATS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                role="menuitem"
                disabled={busy !== null}
                onClick={() => run(key)}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-bold transition hover:bg-[rgba(20,96,107,0.10)] disabled:opacity-50"
                style={{ color: OMS_COLORS.tealDark }}
              >
                {busy === key ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: '#14606B' }} />
                ) : (
                  <Icon className="h-3.5 w-3.5" style={{ color: '#14606B' }} />
                )}
                {label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
